import { HttpParams } from '@angular/common/http';
import { Component, ElementRef, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BasicPage } from '@app/model/basic-page.model';
import { DataResponse } from '@app/model/data-response.model';

import { SettingsService } from '@app/services/settings.service';

import { SidebarSharedService } from '@app/services/sidebar-shared.service';
import {
  CachedHttpService,
  ColorHelperService,
  HttpService,
  ImageModel,
  MenuItemKey,
  NavigationArea,
  NavigationMenuItem,
  NavigationMenuService,
  OfflineModeService,
  SessionDTO,
  SettingsModel
} from 'library-explorer';
import { forkJoin, Observable, Subject } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

@Component({
    selector: 'app-sidebar-menu',
    templateUrl: './sidebar-menu.component.html',
    styleUrls: ['./sidebar-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SidebarMenuComponent implements OnInit, OnDestroy {
  @Input() public currentUser: SessionDTO;
  @Input() public isLogoVisible = false;
  @Input() public headerLogo: ImageModel;
  @Input() public set isLoggedIn(value: boolean) {
    if (value === this._isLoggedIn && this.isInitialized) {
      return;
    }

    this._isLoggedIn = value;

    if (value) {
      this.loadData();
      
      if (!this.isMobile) {
        this.changeOpenState(true);
      }

      return;
    }

    this.loadSettingsOnly();
  }

  public get isLoggedIn() {
    return this._isLoggedIn;
  }


  public opened = false;

  public darkTheme = false;

  public textColor: string;
  public backgroundColor: string;


  public topNavigationMenu: NavigationMenuItem[]  = [];

  public bottomNavigationMenu: NavigationMenuItem[]  = [];

  public isInitialized = false;

  public registrationEnabled = false;
  public nativeNavigationBar$: Observable<boolean>;

  public readonly menuArea: typeof NavigationArea = NavigationArea;

  private get isMobile() {
    return window.matchMedia('(max-width: 991px)').matches;;
  }

  private _isLoggedIn: boolean;

  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private readonly navigationMenuService: NavigationMenuService,
    private readonly router: Router,
    private readonly sharedSidebarService: SidebarSharedService,
    private readonly element: ElementRef,
    private readonly colorHelperService: ColorHelperService,
    private readonly httpService: HttpService,
    private readonly settingsService: SettingsService,
    private readonly cacheHttpService: CachedHttpService,
  ) { }

  ngOnInit(): void {  
    this.sharedSidebarService.navigationSidebarOpened
      .subscribe(data => {
        this.opened = data;

        if (this.opened && this.isMobile) {
          this.scrollToTop();
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public closeMenuIfMobile(): void {
    if (!this.isMobile) {
      return;
    }

    if (!this.opened) {
      return;
    }

    this.changeOpenState(false);
  }

  public changeOpenState(value = !this.opened): void {
    this.opened = value;

    this.sharedSidebarService.mainSidebarTrigger(value);
  }
  private loadData(): void {
    const params = new HttpParams().set('menuName', 'header');

    forkJoin([
      this.cacheHttpService.get<DataResponse<BasicPage>>('menu-items', params),
      this.settingsService.getSettings()
    ])
      .subscribe(([data, settings]) => {
        this.initializeSettings(settings);
        this.initializeMenus(data.items);

        this.isInitialized = true;
      });
  }

  private initializeMenus(additionalMenuItems: BasicPage[] = []): void {
    this.initializeTopNavigationMenu(additionalMenuItems);
    this.initializeBottomNavigationMenu();
  }

  private loadSettingsOnly(): void {
    this.settingsService.getSettings().subscribe(settings => {
      this.initializeSettings(settings);
      this.isInitialized = true;
    })
  }

  private initializeSettings(settings: SettingsModel): void {
    this.initializeColors(settings);
    this.registrationEnabled = this.settingsService.isRegistrationEnabled(settings);
  }

  private initializeColors(settings: SettingsModel): void {
    const { backgroundColor, textColor } = settings.colors.sidebarMenu || {};
    this.darkTheme = this.colorHelperService.isColorDark(backgroundColor);

    if (backgroundColor) {
      this.element.nativeElement.style.setProperty('--lms-sidebar-bg-color', backgroundColor);
    }

    if (textColor) {
      this.element.nativeElement.style.setProperty('--lms-sidebar-text-color', textColor);
    }
  }

  private initializeTopNavigationMenu(additionalMenuItems: BasicPage[] = []): void {
    this.topNavigationMenu = this.navigationMenuService.getTopSidebarMenuItems();

    this.topNavigationMenu.push(...additionalMenuItems.map(item => ({
      title: item.title,
      path: item.url || item.alias,
      iconUrl: item.additionalFields && item.additionalFields.svgIcon?.url
    })));

    this.setUpActivateStateForCatalog();
  }

  private initializeBottomNavigationMenu(): void {
    this.bottomNavigationMenu = this.navigationMenuService.getBottomSidebarMenuItems();
  }

  private setUpActivateStateForCatalog(): void {
    const catalogMenuItem = this.topNavigationMenu.find(item => item.key === MenuItemKey.CATALOG);
  
    const catalogActive = this.sharedSidebarService.isCatalogMenuActive();
    this.setActiveStateForMenuItem(catalogMenuItem, catalogActive);

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
      )
      .subscribe((event: NavigationEnd) => {
        const catalogActive = this.sharedSidebarService.isCatalogMenuActive();
        this.setActiveStateForMenuItem(catalogMenuItem, catalogActive);
      });
  }

  private setActiveStateForMenuItem(item: NavigationMenuItem, active = false): void {
    item.cssClass = active ? 'active' : null;
  }

  private scrollToTop(): void {
    setTimeout(() => {
      this.element.nativeElement.getElementsByClassName('sidebar-navigation')[0]?.scrollTo({ top: 0 });
    })
  }

}
